(() => {
  const initSection = (root) => {
    if (!root || root.dataset.improscarReady === "true") return;
    root.dataset.improscarReady = "true";

    const routesRoot = window.Shopify?.routes?.root || "/";
    const locale = root.dataset.locale || "en-GB";
    const currency = root.dataset.currency || "GBP";
    const drawer = root.querySelector("[data-cart-drawer]");
    const scrim = root.querySelector("[data-cart-scrim]");
    const itemsContainer = root.querySelector("[data-cart-items]");
    const subtotal = root.querySelector("[data-cart-subtotal]");
    const count = root.querySelector("[data-cart-count]");
    const toast = root.querySelector("[data-toast]");
    const nav = root.querySelector("[data-nav]");
    let toastTimer;

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency
        }).format((Number(cents) || 0) / 100);
      } catch (error) {
        return `£${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    };

    const escapeHtml = (value = "") => {
      const div = document.createElement("div");
      div.textContent = value;
      return div.innerHTML;
    };

    const showToast = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add("is-visible");
      clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => {
        toast.classList.remove("is-visible");
      }, 2200);
    };

    const openDrawer = () => {
      drawer?.classList.add("is-open");
      scrim?.classList.add("is-open");
      drawer?.setAttribute("aria-hidden", "false");
      document.documentElement.style.overflow = "hidden";
    };

    const closeDrawer = () => {
      drawer?.classList.remove("is-open");
      scrim?.classList.remove("is-open");
      drawer?.setAttribute("aria-hidden", "true");
      document.documentElement.style.overflow = "";
    };

    const renderCart = (cart) => {
      if (!cart || !itemsContainer) return;

      if (count) {
        count.textContent = cart.item_count;
        count.classList.add("is-pulsing");
        window.setTimeout(() => count.classList.remove("is-pulsing"), 220);
      }

      if (subtotal) subtotal.textContent = money(cart.total_price);

      if (!cart.items.length) {
        itemsContainer.innerHTML =
          '<p class="improscar__empty">Your basket is empty. Add a product to get started.</p>';
        return;
      }

      itemsContainer.innerHTML = cart.items
        .map((item) => {
          const imageSeparator = item.image?.includes("?") ? "&" : "?";
          const image = item.image
            ? `<img src="${escapeHtml(item.image)}${imageSeparator}width=120" alt="">`
            : "";
          const variant = item.variant_title && item.variant_title !== "Default Title"
            ? `<div class="improscar__cart-price">${escapeHtml(item.variant_title)}</div>`
            : "";

          return `
            <div class="improscar__cart-row">
              ${image}
              <div class="improscar__cart-info">
                <div class="improscar__cart-name">${escapeHtml(item.product_title)}</div>
                ${variant}
                <div class="improscar__cart-price">${money(item.final_line_price)}</div>
                <div class="improscar__quantity">
                  <button type="button" aria-label="Decrease ${escapeHtml(item.product_title)} quantity" data-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" aria-label="Increase ${escapeHtml(item.product_title)} quantity" data-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}">+</button>
                </div>
              </div>
            </div>`;
        })
        .join("");
    };

    const fetchCart = async () => {
      const response = await fetch(`${routesRoot}cart.js`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error("Unable to load the basket.");
      const cart = await response.json();
      renderCart(cart);
      return cart;
    };

    const addToCart = async (button) => {
      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.disabled) {
        showToast("Select a Shopify product in the Theme Customiser first.");
        return;
      }

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = "Adding…";

      try {
        const response = await fetch(`${routesRoot}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }]
          })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || "This item could not be added.");

        await fetchCart();
        button.textContent = "Added ✓";
        button.classList.add("is-added");
        showToast(`${button.dataset.productTitle || "Product"} added to basket`);
        openDrawer();
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } catch (error) {
        showToast(error.message || "This item could not be added.");
        button.textContent = originalText;
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
          button.classList.remove("is-added");
        }, 1400);
      }
    };

    root.addEventListener("click", async (event) => {
      const addButton = event.target.closest("[data-add-to-cart]");
      if (addButton) {
        event.preventDefault();
        await addToCart(addButton);
        return;
      }

      if (event.target.closest("[data-open-cart]")) {
        event.preventDefault();
        await fetchCart().catch(() => {});
        openDrawer();
        return;
      }

      if (event.target.closest("[data-close-cart]") || event.target === scrim) {
        closeDrawer();
        return;
      }

      const quantityButton = event.target.closest("[data-cart-change]");
      if (quantityButton) {
        quantityButton.disabled = true;
        try {
          const response = await fetch(`${routesRoot}cart/change.js`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              id: quantityButton.dataset.key,
              quantity: Number(quantityButton.dataset.quantity)
            })
          });
          const cart = await response.json();
          if (!response.ok) throw new Error(cart.description || "The basket could not be updated.");
          renderCart(cart);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        } catch (error) {
          showToast(error.message || "The basket could not be updated.");
          quantityButton.disabled = false;
        }
        return;
      }

      if (event.target.closest("[data-checkout]")) {
        const cart = await fetchCart().catch(() => null);
        if (!cart?.item_count) {
          showToast("Add a product before checking out.");
          return;
        }
        window.location.href = `${routesRoot}checkout`;
        return;
      }

      if (event.target.closest("[data-nav-toggle]")) {
        nav?.classList.toggle("is-open");
      }

      if (event.target.closest("[data-nav] a")) {
        nav?.classList.remove("is-open");
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer?.classList.contains("is-open")) {
        closeDrawer();
      }
    });

    fetchCart().catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-improscar-section]").forEach(initSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
